import { ref, computed } from "vue";

/**
 * Slide management functions for ReportsV2 store
 */
export function createSlidesManager(currentReport, selectedSlide, api) {
  // Track pending changes (local-only until save)
  const pendingNewSlides = ref([]);
  const pendingDeletedSlideIds = ref([]);
  const dirtySlideIds = ref({});
  const dirtyBindings = ref({});
  const pendingReorder = ref(false);

  // Counter for temporary slide IDs (negative to avoid collision with server IDs)
  let tempIdCounter = -1;

  const hasUnsavedChanges = computed(() => {
    return (
      pendingNewSlides.value.length > 0 ||
      pendingDeletedSlideIds.value.length > 0 ||
      Object.keys(dirtySlideIds.value).length > 0 ||
      pendingReorder.value
    );
  });

  const hasSlides = computed(() => currentReport.value?.slides?.length > 0);

  function resetDirtyState() {
    pendingNewSlides.value = [];
    pendingDeletedSlideIds.value = [];
    dirtySlideIds.value = {};
    dirtyBindings.value = {};
    pendingReorder.value = false;
    tempIdCounter = -1;
  }

  function cloneBindings(bindings) {
    return JSON.parse(JSON.stringify(bindings));
  }

  function addSlide(slideTemplateId, bindings = {}) {
    const tempId = tempIdCounter--;
    const position = (currentReport.value.slides?.length || 0) + 1;

    const newSlide = {
      id: tempId,
      slide_template_id: slideTemplateId,
      bindings: cloneBindings(bindings),
      position,
      _isNew: true,
    };

    if (!currentReport.value.slides) {
      currentReport.value.slides = [];
    }
    currentReport.value.slides.push(newSlide);
    pendingNewSlides.value.push(newSlide);
    selectedSlide.value = newSlide;

    return newSlide;
  }

  function updateSlideLocal(slideId, bindings) {
    const bindingsCopy = cloneBindings(bindings);
    dirtyBindings.value[slideId] = bindingsCopy;
    dirtySlideIds.value[slideId] = true;

    const slide = currentReport.value?.slides?.find((s) => s.id === slideId);
    if (slide) {
      slide.bindings = bindingsCopy;
    }
  }

  async function saveSlide(slideId, bindings) {
    const { response, data } = await api.updateSlideData(slideId, { bindings });

    if (response.value.ok) {
      const index = currentReport.value.slides.findIndex((s) => s.id === slideId);
      if (index !== -1) {
        currentReport.value.slides[index] = data.value;
        if (selectedSlide.value?.id === slideId) {
          selectedSlide.value = data.value;
        }
      }
      return data.value;
    } else {
      throw new Error(data.value?.error || "Failed to save slide");
    }
  }

  function removeSlide(slideId) {
    const slide = currentReport.value.slides.find((s) => s.id === slideId);
    if (!slide) return;

    currentReport.value.slides = currentReport.value.slides.filter((s) => s.id !== slideId);

    if (slide._isNew) {
      pendingNewSlides.value = pendingNewSlides.value.filter((s) => s.id !== slideId);
    } else {
      pendingDeletedSlideIds.value.push(slideId);
    }

    delete dirtySlideIds.value[slideId];
    delete dirtyBindings.value[slideId];

    if (selectedSlide.value?.id === slideId) {
      selectedSlide.value = currentReport.value.slides[0] || null;
    }

    currentReport.value.slides.forEach((s, index) => {
      s.position = index + 1;
    });
  }

  function reorderSlides(positions) {
    positions.forEach(({ id, position }) => {
      const slide = currentReport.value.slides.find((s) => s.id === id);
      if (slide) slide.position = position;
    });
    currentReport.value.slides.sort((a, b) => a.position - b.position);
    pendingReorder.value = true;
  }

  function insertSlideAt(slideParams, afterSlideId) {
    const tempId = tempIdCounter--;
    const insertIndex = currentReport.value.slides.findIndex((s) => s.id === afterSlideId) + 1;

    const newSlide = {
      id: tempId,
      slide_template_id: slideParams.slide_template_id,
      bindings: cloneBindings(slideParams.bindings || {}),
      position: insertIndex + 1,
      _isNew: true,
    };

    currentReport.value.slides.splice(insertIndex, 0, newSlide);
    pendingNewSlides.value.push(newSlide);

    currentReport.value.slides.forEach((s, index) => {
      s.position = index + 1;
    });
    pendingReorder.value = true;

    selectedSlide.value = newSlide;
    return newSlide;
  }

  function addSlideAfter(slideTemplateId, afterSlideId) {
    return insertSlideAt({ slide_template_id: slideTemplateId, bindings: {} }, afterSlideId);
  }

  function duplicateSlide(slideId) {
    const slideToClone = currentReport.value.slides.find((s) => s.id === slideId);
    if (!slideToClone) return;

    const bindingsCopy = cloneBindings(slideToClone.bindings || {});
    return insertSlideAt({ slide_template_id: slideToClone.slide_template_id, bindings: bindingsCopy }, slideId);
  }

  function getEffectiveBindings(slide) {
    if (!slide) return {};
    // slide._streaming: dead since #1980, no producer sets it anymore.
    if (slide._streaming || !slide.id) return slide.bindings || {};
    return dirtyBindings.value[slide.id] || slide.bindings || {};
  }

  function selectSlide(slideId) {
    selectedSlide.value = currentReport.value?.slides?.find((s) => s.id === slideId) || null;
  }

  async function saveAllSlides(saving) {
    saving.value = true;
    let allSaved = true;
    try {
      // 1. Delete slides marked for deletion (in parallel)
      const deletions = await Promise.all(
        pendingDeletedSlideIds.value.map((slideId) => api.deleteSlideData(slideId)),
      );
      if (!deletions.every(({ response }) => response.value.ok)) allSaved = false;

      // 2. Create new slides and map temp IDs to real IDs
      for (const newSlide of pendingNewSlides.value) {
        const { response, data } = await api.createSlideData({
          slide_template_id: newSlide.slide_template_id,
          bindings: newSlide.bindings,
        });

        if (response.value.ok) {
          const slideIndex = currentReport.value.slides.findIndex((s) => s.id === newSlide.id);
          if (slideIndex !== -1) {
            currentReport.value.slides[slideIndex] = {
              ...data.value,
              position: newSlide.position,
            };
            if (selectedSlide.value?.id === newSlide.id) {
              selectedSlide.value = currentReport.value.slides[slideIndex];
            }
          }
        } else allSaved = false;
      }

      // 3. Update modified bindings for existing slides
      const bindingPromises = [];
      for (const slideIdStr of Object.keys(dirtySlideIds.value)) {
        const slideId = parseInt(slideIdStr, 10);
        if (slideId < 0) continue;

        const bindings = dirtyBindings.value[slideId];
        if (bindings) {
          bindingPromises.push(saveSlide(slideId, bindings));
        }
      }
      await Promise.all(bindingPromises);

      // 4. Reorder all slides if needed
      if (pendingReorder.value || pendingNewSlides.value.length > 0) {
        const positions = currentReport.value.slides.map((slide, index) => ({
          id: slide.id,
          position: index + 1,
        }));

        const { response } = await api.reorderSlidesData(positions);
        if (!response.value.ok) allSaved = false;

        positions.forEach(({ id, position }) => {
          const slide = currentReport.value.slides.find((s) => s.id === id);
          if (slide) slide.position = position;
        });
      }

      // Clear _isNew flags
      currentReport.value.slides.forEach((s) => {
        delete s._isNew;
      });

      resetDirtyState();
      return allSaved;
    } finally {
      saving.value = false;
    }
  }

  return {
    // State
    pendingNewSlides,
    pendingDeletedSlideIds,
    dirtySlideIds,
    dirtyBindings,
    pendingReorder,
    hasUnsavedChanges,
    hasSlides,
    // Getters
    getEffectiveBindings,
    // Actions
    resetDirtyState,
    addSlide,
    updateSlideLocal,
    saveSlide,
    removeSlide,
    reorderSlides,
    insertSlideAt,
    addSlideAfter,
    duplicateSlide,
    selectSlide,
    saveAllSlides,
  };
}
